import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setAdminLogin, isAdminLoggedIn } from '../utils/storage';
import { getProperties, createProperty, updateProperty, deleteProperty } from '../services/api';
import PropertyForm from '../components/PropertyForm';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const [properties, setProperties] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingProperty, setEditingProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        // Check if admin is logged in
        if (!isAdminLoggedIn()) {
            navigate('/admin/login');
            return;
        }

        // Load properties from API
        const loadProperties = async () => {
            try {
                setLoading(true);
                const loadedProperties = await getProperties();
                if (loadedProperties && loadedProperties.length > 0) {
                    setProperties(loadedProperties);
                } else {
                    setProperties([]);
                }
            } catch (error) {
                console.error('Error loading properties:', error);
                alert('Error loading properties. Make sure the backend server is running on port 5000.');
                setProperties([]);
            } finally {
                setLoading(false);
            }
        };

        loadProperties();
    }, [navigate]);

    const handleAddNew = () => {
        setEditingProperty(null);
        setShowForm(true);
    };

    const handleEdit = (property) => {
        setEditingProperty(property);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this property?')) {
            try {
                await deleteProperty(id);
                // Remove from local state
                setProperties(properties.filter(p => p.id !== id));
                alert('Property deleted successfully!');
            } catch (error) {
                console.error('Error deleting property:', error);
                alert('Error deleting property. Please try again.');
            }
        }
    };

    const handleSave = async (propertyData) => {
        try {
            if (editingProperty) {
                // Update existing property
                const updated = await updateProperty(editingProperty.id, propertyData);
                setProperties(properties.map(p => 
                    p.id === editingProperty.id ? updated : p
                ));
                alert('Property updated successfully!');
            } else {
                // Add new property
                const newProperty = await createProperty(propertyData);
                setProperties([...properties, newProperty]);
                alert('Property added successfully!');
            }
            
            setShowForm(false);
            setEditingProperty(null);
        } catch (error) {
            console.error('Error saving property:', error);
            const errorMessage = error.message || 'Unknown error occurred';
            alert(`Error saving property: ${errorMessage}\n\nMake sure:\n1. Backend server is running on http://localhost:5000\n2. All required fields are filled\n3. Check browser console for details`);
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingProperty(null);
    };

    const handleLogout = () => {
        setAdminLogin(false);
        navigate('/admin/login');
    };

    const handleRefresh = async () => {
        try {
            setLoading(true);
            const loadedProperties = await getProperties();
            if (loadedProperties && loadedProperties.length > 0) {
                setProperties(loadedProperties);
                alert('Properties refreshed successfully!');
            }
        } catch (error) {
            console.error('Error refreshing properties:', error);
            alert('Error refreshing properties. Make sure the backend server is running.');
        } finally {
            setLoading(false);
        }
    };

    if (!isAdminLoggedIn()) {
        return null;
    }

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <div className="container">
                    <div className="admin-header-content">
                        <h1>Admin Dashboard</h1>
                        <div className="admin-actions">
                            <a href="/" className="view-site-btn">View Site</a>
                            <button onClick={handleLogout} className="logout-btn">Logout</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="admin-content">
                <div className="container">
                    {!showForm ? (
                        <>
                            <div className="dashboard-header">
                                <h2>Property Management</h2>
                                <div className="dashboard-actions">
                                    <button onClick={handleRefresh} className="export-btn" disabled={loading}>
                                        <i className="fas fa-sync-alt"></i> {loading ? 'Loading...' : 'Refresh'}
                                    </button>
                                    <button onClick={handleAddNew} className="add-property-btn">
                                        <i className="fas fa-plus"></i> Add New Property
                                    </button>
                                </div>
                            </div>

                            <div className="properties-table-container">
                                {loading ? (
                                    <div className="empty-state">
                                        <i className="fas fa-spinner fa-spin" style={{ fontSize: '3rem', color: '#1a73e8' }}></i>
                                        <h3>Loading Properties...</h3>
                                        <p>Please wait while we fetch properties from the database</p>
                                    </div>
                                ) : properties.length === 0 ? (
                                    <div className="empty-state">
                                        <i className="fas fa-inbox"></i>
                                        <h3>No Properties</h3>
                                        <p>Click "Add New Property" to get started</p>
                                    </div>
                                ) : (
                                    <table className="properties-table">
                                        <thead>
                                            <tr>
                                                <th>Image</th>
                                                <th>Title</th>
                                                <th>Location</th>
                                                <th>Type</th>
                                                <th>Price</th>
                                                <th>Bedrooms</th>
                                                <th>Bathrooms</th>
                                                <th>Area</th>
                                                <th>Badge</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {properties.map(property => (
                                                <tr key={property.id}>
                                                    <td>
                                                        <div className="property-image-cell">
                                                            {property.image}
                                                        </div>
                                                    </td>
                                                    <td>{property.title}</td>
                                                    <td>{property.location}</td>
                                                    <td className="property-type">{property.type}</td>
                                                    <td>${property.price.toLocaleString()}</td>
                                                    <td>{property.bedrooms}</td>
                                                    <td>{property.bathrooms}</td>
                                                    <td>{property.area} sqft</td>
                                                    <td>
                                                        <span className="property-badge-cell">{property.badge}</span>
                                                    </td>
                                                    <td>
                                                        <div className="action-buttons">
                                                            <button 
                                                                onClick={() => handleEdit(property)}
                                                                className="edit-btn"
                                                                title="Edit"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </button>
                                                            <button 
                                                                onClick={() => handleDelete(property.id)}
                                                                className="delete-btn"
                                                                title="Delete"
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="dashboard-stats">
                                <div className="stat-card">
                                    <i className="fas fa-home"></i>
                                    <h3>{properties.length}</h3>
                                    <p>Total Properties</p>
                                </div>
                                <div className="stat-card">
                                    <i className="fas fa-building"></i>
                                    <h3>{properties.filter(p => p.type === 'apartment').length}</h3>
                                    <p>Apartments</p>
                                </div>
                                <div className="stat-card">
                                    <i className="fas fa-home"></i>
                                    <h3>{properties.filter(p => p.type === 'villa').length}</h3>
                                    <p>Villas</p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <PropertyForm
                            property={editingProperty}
                            onSave={handleSave}
                            onCancel={handleCancel}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
